import { useEffect, useId, useRef, useState } from 'react';
import { Dialog } from '@240tutoring/labs-ui/dialog';
import type { CollabMaterialListing } from '../../core/collab';
import type { CollabAccess } from '../api';

/** A pause this long after the last key sends the search: one request per pause, not per letter. */
export const SEARCH_DEBOUNCE_MS = 250;

type Result = { q: string; materials: CollabMaterialListing[] } | { q: string; failed: true };

/**
 * "Attach from Collab" (R3, R10, AE1, AE5): the teacher's materials across every Collab group,
 * newest first, each with its group's name, filtered by title as they type. Any number can be
 * chosen, up to the item's room, and stay listed above the results across searches; those
 * already attached show as attached. When Collab cannot
 * answer, it says so, and the item form behind it still saves. Titles and group names are
 * teachers' words, so the dialog stays out of session replay.
 */
export function CollabPicker({ search, attached, room, onAttach, onClose }: {
  search: CollabAccess['search']; attached: ReadonlySet<string>; room: number;
  onAttach(picked: CollabMaterialListing[]): void; onClose(): void;
}) {
  const id = useId();
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<Result | null>(null);
  const [chosen, setChosen] = useState<CollabMaterialListing[]>([]);
  // The caller may build a new `search` each render; only the query starts a request.
  const searchRef = useRef(search);
  searchRef.current = search;

  const q = query.trim();
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      searchRef.current(q, controller.signal)
        .then(materials => { if (!controller.signal.aborted) setResult({ q, materials }); })
        .catch(() => { if (!controller.signal.aborted) setResult({ q, failed: true }); });
    }, q ? SEARCH_DEBOUNCE_MS : 0);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [q]);

  const current = result?.q === q ? result : null;
  const isChosen = (m: CollabMaterialListing) => chosen.some(c => c.id === m.id);
  const toggle = (m: CollabMaterialListing) => setChosen(c => (isChosen(m) ? c.filter(x => x.id !== m.id) : [...c, m]));
  const full = chosen.length >= room;

  return <Dialog titleId={`${id}-title`} onClose={onClose} className="picker-dialog collab-picker ph-no-capture">
    <div data-ph-no-capture>
      <h2 id={`${id}-title`}>Attach from Collab</h2>
      <p className="muted">Materials from every Collab group you are in, newest first.</p>
      <label htmlFor={`${id}-search`}>Search by title</label>
      <input id={`${id}-search`} type="search" value={query} autoFocus autoComplete="off" onChange={e => setQuery(e.target.value)} />
      {/* What is chosen stays on screen while the search changes, each removable here. */}
      {chosen.length > 0 && <>
        <p id={`${id}-chosen`} className="collab-chosen-heading">Chosen</p>
        <ul className="day-item-materials collab-chosen" aria-labelledby={`${id}-chosen`}>
          {chosen.map(m => <li key={m.id}>
            <span className="day-item-material-name">{m.title}<small>{m.groupName}</small></span>
            <span className="day-item-material-tools">
              <button type="button" className="text-button" aria-label={`Remove ${m.title}`} onClick={() => toggle(m)}>Remove</button>
            </span>
          </li>)}
        </ul>
      </>}
      {current === null && <p className="muted" role="status">Loading your materials…</p>}
      {current && 'failed' in current && <p className="error-message" role="alert">Collab is unavailable right now. You can still save the item and attach later.</p>}
      {current && 'materials' in current && current.materials.length === 0 && <p className="muted" role="status">
        {q ? `No materials match "${q}".` : 'You have no materials in Collab yet. Upload a file instead.'}
      </p>}
      {current && 'materials' in current && current.materials.length > 0 && <ul className="collab-results" aria-label="Collab materials">
        {current.materials.map(m => {
          const already = attached.has(m.id);
          const picked = already || isChosen(m);
          return <li key={m.id}>
            <label className={`source-option${picked ? ' selected' : ''}`}>
              <input type="checkbox" checked={picked} disabled={already || (!picked && full)} onChange={() => toggle(m)} />
              <span><strong>{m.title}</strong><small>{m.groupName}{already ? ' · Attached' : ''}</small></span>
            </label>
          </li>;
        })}
      </ul>}
      {room === 0 && <p className="muted">This item has ten attachments, the most it can hold.</p>}
      <div className="dialog-actions">
        <button className="secondary" type="button" onClick={onClose}>Cancel</button>
        <button className="primary" type="button" disabled={chosen.length === 0} onClick={() => onAttach(chosen)}>
          {chosen.length === 0 ? 'Attach' : `Attach ${chosen.length}`}
        </button>
      </div>
    </div>
  </Dialog>;
}
